import React from "react"
import styled from "styled-components"
import { LayoutCardLoader, LayoutCardEmpty } from "components"
import { Card, List } from "antd"
import { IConfigurationContext } from "contexts/configuration/context"
import { IMoney } from "platform-client/types"
import { MoneyDisplay } from "components/display/MoneyDisplay"
import { IPricingRateableComponentPrice } from "contexts/opus/context"
import { RoadsideAssistanceMappings } from "utils/data/stella-roadside-mapping"
import { QuoteType } from "platform-client/types/quote"
import { BrokerCommissionAdjuster } from "./broker-commission-adjuster"

const ListStyledFooter = styled.div`
  padding: 8px;
  display: flex;
  background-color: #fafafa;
  justify-content: space-between;
  font-weight: 600;
`

export const QuotePremium: React.FC<IQuotePremium> = (props) => {
  const cardTitle = props.quoteType === "MTA" ? "Premium Adjustment" : "Premium"

  if (props.isWorking) {
    return <LayoutCardLoader title={cardTitle} />
  }
  if (props.isQuoteConverted === true && props.quoteType === "MTA") {
    return (
      <Card title={cardTitle}>
        <div>
          <p>
            This quote has been converted, see documents for the premium
            adjustment.
          </p>
        </div>
      </Card>
    )
  }

  if (props.netPremium && props.grossPremium) {
    let baseInsurancePremium = props.netPremium
    if (props.brokerCommission) {
      baseInsurancePremium = {
        value: props.netPremium.value - props.brokerCommission.value,
        currencyCode: props.netPremium.currencyCode
      }
    }

    return (
      <Card title={cardTitle}>
        <List
          itemLayout="horizontal"
          size="small"
          footer={
            <ListStyledFooter>
              <span>Total</span>
              <span>
                <MoneyDisplay value={props.grossPremium} />
              </span>
            </ListStyledFooter>
          }
        >
          <List.Item style={{ padding: 8 }}>
            <List.Item.Meta description="Base premium" />
            <div>
              <MoneyDisplay value={baseInsurancePremium} />
            </div>
          </List.Item>

          {props.brokerCommission && (
            <List.Item style={{ padding: 8 }}>
              <List.Item.Meta description="Broker Commission" />
              <BrokerCommissionAdjuster
                amount={props.brokerCommission}
                currentCommissionPercent={props.brokerCommissionPercent}
                maxCommissionPercent={props.maxBrokerCommissionPercent}
                isSubmitting={props.isFlexingBrokerCommission}
                // Editing (the Adjust affordance) stays restricted to New Business
                // quotes in the Quoted state - see canFlexBrokerCommission in
                // sub-views/summary. The percentage itself is shown for every
                // quote type/state.
                onSubmit={
                  props.canFlexBrokerCommission
                    ? props.onFlexBrokerCommission
                    : undefined
                }
              />
            </List.Item>
          )}

          <List.Item style={{ padding: 8 }}>
            {props.configuration.quote.showIPTLabel && props.iptPercentage ? (
              <List.Item.Meta
                description={`IPT ${props.iptPercentage * 100}%`}
              />
            ) : (
              <List.Item.Meta description="Total Tax" />
            )}

            <div>
              <MoneyDisplay value={props.taxTotal} />
            </div>
          </List.Item>

          {props.rateableComponentFinalPremium
            ?.filter((re) => re.isStandaloneProduct === true)
            ?.map((re) => {
              const rcLabel =
                RoadsideAssistanceMappings[re.referenceID] || re.referenceID

              return (
                <List.Item style={{ padding: 8 }}>
                  <List.Item.Meta description={rcLabel} />
                  <div>
                    <MoneyDisplay value={re.price.gross} />
                  </div>
                </List.Item>
              )
            })}

          {props.fees && (
            <List.Item style={{ padding: 8 }}>
              <List.Item.Meta description="Underwriting Fee" />
              <div>
                <MoneyDisplay value={props.fees} />
              </div>
            </List.Item>
          )}
        </List>
      </Card>
    )
  }

  return <LayoutCardEmpty title={cardTitle} />
}

export interface IQuotePremium {
  configuration: IConfigurationContext
  isWorking: boolean
  quoteType: QuoteType
  isQuoteConverted: boolean
  // isLoaded: boolean
  netPremium?: IMoney
  grossPremium?: IMoney
  iptPercentage?: number
  taxTotal?: IMoney
  fees?: IMoney
  brokerCommission?: IMoney | undefined
  rateableComponentFinalPremium: IPricingRateableComponentPrice[]
  canFlexBrokerCommission?: boolean
  isFlexingBrokerCommission?: boolean
  onFlexBrokerCommission?: (commissionPercent: number) => Promise<void>
  brokerCommissionPercent?: number
  maxBrokerCommissionPercent?: number
}
